import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { BdPageable, BdPageableHelper, Paginator } from '@bindoc-projects/platform-lib/data';
import { Observable, of } from 'rxjs';
import {
  BdCompany,
  BdCompanyGroup,
  GroupMemberType,
} from '@bindoc-projects/resource-domain-lib';
import { BdPermissionsCompanyAdminStore } from '../store/company/company-admin.store';
import type { BdGraphApiUser } from '../users/user.model';
import { map, switchMap } from 'rxjs/operators';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';

/** Page size used when walking the company's groups to completion. */
const COMPANY_GROUPS_PAGE_SIZE = 100;

@Injectable({
  providedIn: 'root',
})
export class BdPermissionsCompanyAdminService {
  private _httpClient = inject(HttpClient);
  private _environmentService = inject(BdAppEnvironmentService);

  private _store: BdPermissionsCompanyAdminStore;

  constructor() {
    this._store = new BdPermissionsCompanyAdminStore(
      this._environmentService.load(ApiEndpointKey.BD_PERMISSION_RESOURCE_URL),
    );
  }

  public search(page?: Paginator): Observable<BdPageable<BdCompany>> {
    const url = this._store.getAll();
    const queryParams = page?.appendTo(new HttpParams()) ?? new HttpParams();

    return this._httpClient
      .get<BdPageable<BdCompany>>(url, { params: queryParams })
      .pipe(
        map((response) =>
          BdPageableHelper.responseToPage(response, (item) => item),
        ),
      );
  }

  public getById(companyId: string): Observable<BdCompany> {
    const url = this._store.getByCompanyId(companyId);
    return this._httpClient.get<BdCompany>(url);
  }

  public getGroups(
    companyId: string,
    page?: Paginator,
  ): Observable<BdPageable<BdCompanyGroup>> {
    const url = this._store.getGroupsByCompanyId(companyId);
    const queryParams = page?.appendTo(new HttpParams()) ?? new HttpParams();

    return this._httpClient.get<BdPageable<BdCompanyGroup>>(url, {
      params: queryParams,
    });
  }

  /**
   * Every group of the company, following the paginated endpoint to the end.
   *
   * Needed by the "remove from all groups of this company" action, which has to
   * intersect against the company's *complete* group list — a single page would
   * silently leave the user in the groups that did not fit on it.
   */
  public getAllGroups(companyId: string): Observable<BdCompanyGroup[]> {
    return this._collectGroups(companyId, 0, []);
  }

  private _collectGroups(
    companyId: string,
    pageIndex: number,
    collected: BdCompanyGroup[],
  ): Observable<BdCompanyGroup[]> {
    const page = Paginator.of({
      pageSize: COMPANY_GROUPS_PAGE_SIZE,
      pageIndex,
    });

    return this.getGroups(companyId, page).pipe(
      switchMap((response) => {
        const content = response.content ?? [];
        const total = [...collected, ...content];
        const isLastPage =
          response.last === true ||
          content.length === 0 ||
          total.length >= (response.totalElements ?? total.length);

        return isLastPage
          ? of(total)
          : this._collectGroups(companyId, pageIndex + 1, total);
      }),
    );
  }

  public getMembers(
    companyId: string,
    page?: Paginator,
  ): Observable<BdPageable<BdGraphApiUser>> {
    const url = this._store.getMembersByCompanyId(companyId);
    const queryParams = page?.appendTo(new HttpParams()) ?? new HttpParams();

    return this._httpClient.get<BdPageable<BdGraphApiUser>>(url, {
      params: queryParams,
    });
  }

  public addUserToCompany(
    userId: string,
    companyId: string,
    memberType: GroupMemberType = 'READER',
  ): Observable<{
    user: BdGraphApiUser;
    company: BdCompany;
    localLastModified: Date;
    active: boolean;
    memberSince: Date;
  }> {
    const url = this._store.getMembersByCompanyId(companyId);
    const body: {
      userId: string;
      type: GroupMemberType;
    } = {
      userId,
      type: memberType,
    };

    return this._httpClient.post<{
      user: BdGraphApiUser;
      company: BdCompany;
      localLastModified: Date;
      active: boolean;
      memberSince: Date;
    }>(url, body);
  }

  public removeUserFromCompany(
    userId: string,
    companyId: string,
  ): Observable<void> {
    const url = `${this._store.getMembersByCompanyId(companyId)}${userId}/`;

    return this._httpClient.delete<void>(url);
  }

  /**
   * Renames the company via `PUT /companies/{id}`.
   *
   * The name is the only mutable field, and the write goes to Azure B2C — so it
   * is not a local-only change like {@link setMemberRole} would be. The
   * response carries the updated company, but callers should still re-read what
   * they display: the name is resolved in several places that hold their own
   * copies.
   *
   * ⚠️ The API documents **no** failure statuses for this endpoint, only 200.
   * Callers must therefore not present status-specific copy as authoritative.
   */
  public update(companyId: string, name: string): Observable<BdCompany> {
    const url = this._store.getByCompanyId(companyId);

    return this._httpClient.put<BdCompany>(url, { name });
  }

  public create(name: string): Observable<BdCompany> {
    const url = this._store.index();

    return this._httpClient.post<BdCompany>(url, {
      name: name,
    });
  }
}
